import React from 'react';
import { LinearProgress, Stack, Typography } from '@mui/material';
import { GraduationProgress, getEstimateText } from './studentGraduation';

interface GraduationProgressBarProps {
  progress: GraduationProgress;
  compact?: boolean;
  showEstimate?: boolean;
}

const getProgressTitle = (progress: GraduationProgress) =>
  progress.isReviewReady ? 'Revisión manual para cinturón' : `Siguiente: ${progress.objective}`;

const getProgressDetail = (progress: GraduationProgress) =>
  progress.isReviewReady ? '4º grado' : `${progress.currentClasses} / ${progress.targetClasses} clases`;

export default function GraduationProgressBar({
  progress,
  compact = false,
  showEstimate = false,
}: GraduationProgressBarProps) {
  const isReady = progress.isReviewReady || progress.percentage >= 100;

  if (compact) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.25, sm: 1 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 0.5 }}
        >
          <Typography variant="caption" sx={{ color: '#333333', fontWeight: 700, lineHeight: 1.25 }}>
            {getProgressTitle(progress)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isReady ? '#1d6b32' : 'text.secondary',
              fontWeight: 700,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
            }}
          >
            {progress.percentage}%
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <LinearProgress
            variant="determinate"
            value={progress.percentage}
            sx={{
              flex: 1,
              height: 7,
              borderRadius: 4,
              bgcolor: '#e8e5df',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: isReady ? '#2e7d32' : '#111111',
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 86, textAlign: 'right', lineHeight: 1.25, flexShrink: 0 }}
          >
            {getProgressDetail(progress)}
          </Typography>
        </Stack>
      </>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <LinearProgress
          variant="determinate"
          value={progress.percentage}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            bgcolor: '#e8e5df',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              bgcolor: isReady ? '#2e7d32' : '#111111',
            },
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 34, textAlign: 'right', flexShrink: 0 }}>
          {progress.percentage}%
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 80, flexShrink: 0 }}
        >
          {getProgressDetail(progress)}
        </Typography>
      </Stack>

      {showEstimate && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {getEstimateText(progress.percentage, progress.isReviewReady)}
        </Typography>
      )}
    </>
  );
}
